import React, { useState, useEffect, useCallback, useRef } from 'react';
import _startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { ControlBarButton, palette, Icon } from '@m12s/component-library';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
/* global globalThis */
import { bars, cog, infoCircle, signIn, random, globe, bolt } from 'lib/icons';
import Routes from 'lib/utils/routes';
import TokenStore from 'lib/api/links/TokenStore';
import { throwErrorToast } from 'lib/utils/toast';

import { getMachine } from 'lib/selectors/getMachine';
import { getIsOperatorTrackingEnabled } from 'lib/selectors/getIsOperatorTrackingEnabled';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getCompany } from 'lib/selectors/getCompany';
import {
  actionOpenLanguageModal,
  actionToggleTraining,
  actionOpenLaborTicketModal,
} from 'lib/actions';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { getCanCloseLaborTicket } from 'lib/selectors/getCanOpenLaborTicket';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import useSelectOperator from 'lib/hooks/useSelectOperator';

import {
  Root,
  Menu,
  MenuList,
  MenuItem,
  MenuIcon,
  MenuLink,
  MenuButton,
} from './styled';

const UtilityMenu = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ref = useRef();
  const history = useHistory();
  const laborTicketEnabled = useIsLaborTicketFlow();
  const { openSelectOperator } = useSelectOperator();
  const [open, setOpen] = useState(false);

  const operator = useSelector(getCurrentOperator);
  const canCloseLaborTicket = useSelector(getCanCloseLaborTicket);
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);

  const machine = useSelector(getMachine);
  const isOperatorTrackingEnabled = useSelector(getIsOperatorTrackingEnabled);
  const showAddJob = useSelector(getIsApmERPJobFlow);
  const company = useSelector(getCompany);
  const stopOperator = useOperatorLogout(() => {
    setOpen(false);
  });

  const isKiosk = globalThis.ShopPulseKiosk !== undefined;
  const presetMachineToken = new TokenStore('presetMachine');
  const machineSwitchingDisabled = !company?.machineSwitching;
  const onClickOutside = useCallback((event) => {
    if (!ref.current.contains(event.target)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      return document.removeEventListener('mousedown', onClickOutside);
    };
  }, [onClickOutside]);

  useEffect(() => {
    if (!isKiosk) return undefined;

    const sendHeartbeat = () => {
      if (globalThis.ShopPulseKiosk.setOperatorViewReady) {
        globalThis.ShopPulseKiosk.setOperatorViewReady(true);
      }
    };

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 2000);

    return () => {
      clearInterval(intervalId);
      if (globalThis.ShopPulseKiosk.setOperatorViewReady) {
        globalThis.ShopPulseKiosk.setOperatorViewReady(false);
      }
    };
  }, [isKiosk]);

  return (
    <Root id="utility-menu" ref={ref}>
      <ControlBarButton
        id="utility-menu"
        icon={bars}
        iconSize="2.5rem"
        fitToContent
        onClick={() => {
          setOpen(!open);
        }}
        backgroundColor={open ? palette.Grey300 : palette.White}
      />
      {open && (
        <Menu>
          <MenuList>
            {!isJobLaborTrackingEnabled &&
              isOperatorTrackingEnabled &&
              operator && (
                <MenuItem>
                  <MenuButton
                    onClick={() => {
                      if (laborTicketEnabled && canCloseLaborTicket) {
                        history.push(Routes.machineIdHomePath(machine.id));
                        dispatch(
                          actionOpenLaborTicketModal(
                            LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT
                          )
                        );
                        return;
                      }
                      stopOperator();
                    }}
                    id="sign-out"
                  >
                    <MenuIcon>
                      <Icon color={palette.Grey600} icon={signIn} />
                    </MenuIcon>
                    {_startCase(t('sign out'))}
                  </MenuButton>
                </MenuItem>
              )}
            {isOperatorTrackingEnabled && !operator && (
              <MenuItem>
                <MenuLink
                  onClick={() => {
                    return openSelectOperator();
                  }}
                  id="select-user"
                >
                  <MenuIcon>
                    <Icon color={palette.Grey600} icon={signIn} />
                  </MenuIcon>
                  {_startCase(t('select user'))}
                </MenuLink>
              </MenuItem>
            )}
            {!machineSwitchingDisabled && (
              <MenuItem>
                <MenuLink
                  onClick={() => {
                    presetMachineToken.clear();
                  }}
                  to={Routes.switchMachinesPath()}
                  id="switch-machine"
                >
                  <MenuIcon>
                    <Icon color={palette.Grey600} icon={random} />
                  </MenuIcon>
                  {_startCase(t('switch machine'))}
                </MenuLink>
              </MenuItem>
            )}
            {showAddJob && (
              <MenuItem>
                <MenuButton
                  onClick={() => {
                    if (!operator) {
                      throwErrorToast(
                        t('Please sign in before adding a job.'),
                        { autoClose: 2500 }
                      );
                      return;
                    }
                    setOpen(false);
                    window.dispatchEvent(new CustomEvent('apm:add-job'));
                  }}
                  id="add-job"
                >
                  <MenuIcon>
                    <Icon color={palette.Grey600} icon={bolt} />
                  </MenuIcon>
                  {_startCase(t('add job'))}
                </MenuButton>
              </MenuItem>
            )}
            <MenuItem>
              <MenuButton
                onClick={() => {
                  return dispatch(
                    actionToggleTraining({ toggleTraining: true })
                  );
                }}
                id="training-pdf"
              >
                <MenuIcon>
                  <Icon color={palette.Grey600} icon={infoCircle} />
                </MenuIcon>
                {_startCase(t('Quick Start Guide'))}
              </MenuButton>
            </MenuItem>
            <MenuItem>
              <MenuButton
                onClick={() => {
                  return dispatch(actionOpenLanguageModal());
                }}
                id="select-language"
              >
                <MenuIcon>
                  <Icon color={palette.Grey600} icon={globe} />
                </MenuIcon>
                {_startCase(t('select language'))}
              </MenuButton>
            </MenuItem>
            <MenuItem>
              <MenuLink
                to={Routes.machineIdSelectWorkflowPath(machine.id)}
                id="workflow-trigger"
              >
                <MenuIcon>
                  <Icon color={palette.Grey600} icon={bolt} />
                </MenuIcon>
                {_startCase(t('workflows'))}
              </MenuLink>
            </MenuItem>
            {isKiosk && (
              <MenuItem>
                <MenuButton
                  onClick={() => {
                    globalThis.ShopPulseKiosk.openSettings();
                    setOpen(false);
                  }}
                  id="kiosk-settings"
                >
                  <MenuIcon>
                    <Icon color={palette.Grey600} icon={cog} />
                  </MenuIcon>
                  {_startCase(t('kiosk settings'))}
                </MenuButton>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      )}
    </Root>
  );
};

export { UtilityMenu };
